import { ref, reactive } from 'vue'
import { getAgentInfoByAgentId } from '@/api/portal/user'
import { getAgentFromUrl, saveAgentToStorage, getAgentFromStorage, clearAgentFromStorage } from '@/apps/portal/utils/agentUtils'

// 代理信息状态
const agentInfo = reactive({
  agentId: null,
  siteTitle: null,
  siteSubtitle: null,
  logo: null,
  qrcode: null,
  loaded: false // 是否已加载
})

// 默认值
const defaultAgentInfo = {
  siteTitle: '大象魔法AIGC',
  siteSubtitle: '魔法在手，驰骋全球',
  logo: null, // 使用相对路径，在组件中处理
  qrcode: null // 使用相对路径，在组件中处理
}

/**
 * 获取代理信息
 * @param {string} agentId - agent ID
 * @returns {Promise<void>}
 */
export async function loadAgentInfo(agentId) {
  if (!agentId) {
    // 如果没有agentId，使用默认值
      Object.assign(agentInfo, {
        agentId: null,
        siteTitle: defaultAgentInfo.siteTitle,
        siteSubtitle: defaultAgentInfo.siteSubtitle,
        logo: defaultAgentInfo.logo,
        qrcode: defaultAgentInfo.qrcode,
        loaded: true
      })
    return
  }

  try {
    const res = await getAgentInfoByAgentId(agentId)
    if (res && res.code === 0) {
      // 兼容两种返回格式：res.data 或直接在 res 根级别
      const data = res.data || res
      Object.assign(agentInfo, {
        agentId: data.agentId || agentId,
        siteTitle: data.siteTitle || defaultAgentInfo.siteTitle,
        siteSubtitle: data.siteSubtitle || defaultAgentInfo.siteSubtitle,
        logo: data.logo || null,
        qrcode: data.qrcode || null,
        loaded: true
      })
      // 保存到localStorage
      saveAgentToStorage(agentId)
      console.log('代理信息加载成功:', agentInfo)
    } else {
      // 如果获取失败，使用默认值
      Object.assign(agentInfo, {
        agentId: agentId,
        siteTitle: defaultAgentInfo.siteTitle,
        siteSubtitle: defaultAgentInfo.siteSubtitle,
        logo: defaultAgentInfo.logo,
        qrcode: defaultAgentInfo.qrcode,
        loaded: true
      })
    }
  } catch (error) {
    console.error('获取代理信息失败:', error)
    // 如果获取失败，使用默认值
    Object.assign(agentInfo, {
      agentId: agentId,
      siteTitle: defaultAgentInfo.siteTitle,
        siteSubtitle: defaultAgentInfo.siteSubtitle,
      logo: defaultAgentInfo.logo,
      qrcode: defaultAgentInfo.qrcode,
      loaded: true
    })
  }
}

/**
 * 初始化代理信息（只从URL获取，如果URL中没有则使用默认值）
 */
export async function initAgentInfo() {
  // 只从URL获取agent参数
  const agentId = getAgentFromUrl()
  
  if (agentId) {
    // 如果URL中有agent参数，加载代理信息并保存到localStorage
    await loadAgentInfo(agentId)
  } else {
    // 如果URL中没有agent参数，清除代理信息，使用默认值
    clearAgentFromStorage()
    await loadAgentInfo(null)
  }
}

/**
 * 获取当前代理信息
 * @returns {object} 代理信息对象
 */
export function getAgentInfo() {
  return agentInfo
}

/**
 * 获取网站标题
 * @returns {string}
 */
export function getSiteTitle() {
  return agentInfo.siteTitle || defaultAgentInfo.siteTitle
}

/**
 * 获取网站副标题
 * @returns {string}
 */
export function getSiteSubtitle() {
  return agentInfo.siteSubtitle || defaultAgentInfo.siteSubtitle
}

/**
 * 获取Logo
 * @returns {string}
 */
export function getLogo() {
  return agentInfo.logo || null
}

/**
 * 获取客服二维码
 * @returns {string}
 */
export function getQrcode() {
  return agentInfo.qrcode || null
}

/**
 * 从用户信息中提取并设置代理信息
 * @param {object} userInfo - 用户信息对象
 * @returns {void}
 */
export function setAgentInfoFromUserInfo(userInfo) {
  if (!userInfo) {
    // 如果用户信息为空，使用默认值
    Object.assign(agentInfo, {
      agentId: null,
      siteTitle: defaultAgentInfo.siteTitle,
      siteSubtitle: defaultAgentInfo.siteSubtitle,
      logo: defaultAgentInfo.logo,
      qrcode: defaultAgentInfo.qrcode,
      loaded: true
    })
    clearAgentFromStorage()
    return
  }

  const agentId = userInfo.agentId || null
  
  if (agentId) {
    // 如果用户有代理ID，优先使用用户信息中的代理站点信息
    // 如果用户信息中没有站点信息，则使用默认值
    Object.assign(agentInfo, {
      agentId: agentId,
      siteTitle: userInfo.agentSiteTitle || defaultAgentInfo.siteTitle,
      siteSubtitle: userInfo.agentSiteSubtitle || defaultAgentInfo.siteSubtitle,
      logo: userInfo.agentSiteLogo || defaultAgentInfo.logo,
      qrcode: userInfo.agentSiteQrcode || defaultAgentInfo.qrcode,
      loaded: true
    })
    // 保存到localStorage
    saveAgentToStorage(agentId)
    console.log('从用户信息设置代理信息成功:', agentInfo)
  } else {
    // 如果用户没有代理ID，使用默认值
    Object.assign(agentInfo, {
      agentId: null,
      siteTitle: defaultAgentInfo.siteTitle,
      siteSubtitle: defaultAgentInfo.siteSubtitle,
      logo: defaultAgentInfo.logo,
      qrcode: defaultAgentInfo.qrcode,
      loaded: true
    })
    clearAgentFromStorage()
    console.log('用户无代理信息，使用默认值')
  }
}
